import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiLoader, FiSave, FiAlertCircle, FiToggleLeft, FiToggleRight } = FiIcons;

const OrderOptionsAdmin = () => {
  const [orderConfig, setOrderConfig] = useState({
    burger: {
      pickup_enabled: true,
      delivery_enabled: true
    },
    pizza: {
      pickup_enabled: true,
      delivery_enabled: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadOrderConfig();
  }, []);

  const loadOrderConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_config')
        .select('*')
        .eq('key', 'order_options')
        .single();

      if (error) {
        console.error('Error loading order config:', error);
        setMessage({ type: 'error', text: 'Fehler beim Laden der Bestelloptionen.' });
        return;
      }

      if (data && data.value) {
        setOrderConfig(data.value);
        console.log('Loaded order config:', data.value);
      }
    } catch (error) {
      console.error('Failed to load order config:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Bestelloptionen.' });
    } finally {
      setLoading(false);
    }
  };

  const saveOrderConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { error } = await supabase
        .from('app_config')
        .update({
          value: orderConfig,
          updated_at: new Date().toISOString()
        })
        .eq('key', 'order_options');

      if (error) {
        throw error;
      }

      setMessage({ type: 'success', text: 'Bestelloptionen erfolgreich gespeichert!' });
    } catch (error) {
      console.error('Error saving order config:', error);
      setMessage({ type: 'error', text: `Fehler beim Speichern: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  const toggleOption = (productType, option) => {
    setOrderConfig(prev => ({
      ...prev,
      [productType]: {
        ...prev[productType],
        [option]: !prev[productType][option]
      }
    }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Bestelloptionen Konfiguration</h2>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            <SafeIcon
              icon={message.type === 'success' ? FiCheck : FiAlertCircle}
              className={`mr-2 ${message.type === 'success' ? 'text-green-500' : 'text-red-500'}`}
            />
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-orange-500 text-xl mb-2" />
          <p className="text-gray-500">Lade Bestelloptionen...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Burger Options */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4">
              <h3 className="text-xl font-bold">🍔 Wunsch-Burger Bestelloptionen</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div>
                  <h4 className="font-semibold text-lg">Abholung</h4>
                  <p className="text-gray-600 text-sm">Ermöglicht Kunden, ihre Burger zur Abholung zu bestellen</p>
                </div>
                <button
                  onClick={() => toggleOption('burger', 'pickup_enabled')}
                  className={`p-2 rounded-full ${
                    orderConfig.burger.pickup_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <SafeIcon
                    icon={orderConfig.burger.pickup_enabled ? FiToggleRight : FiToggleLeft}
                    className="text-2xl"
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div>
                  <h4 className="font-semibold text-lg">Lieferung</h4>
                  <p className="text-gray-600 text-sm">Ermöglicht Kunden, ihre Burger liefern zu lassen</p>
                </div>
                <button
                  onClick={() => toggleOption('burger', 'delivery_enabled')}
                  className={`p-2 rounded-full ${
                    orderConfig.burger.delivery_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <SafeIcon
                    icon={orderConfig.burger.delivery_enabled ? FiToggleRight : FiToggleLeft}
                    className="text-2xl"
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Pizza Options */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4">
              <h3 className="text-xl font-bold">🍕 Wunsch-Pizza Bestelloptionen</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div>
                  <h4 className="font-semibold text-lg">Abholung</h4>
                  <p className="text-gray-600 text-sm">Ermöglicht Kunden, ihre Pizza zur Abholung zu bestellen</p>
                </div>
                <button
                  onClick={() => toggleOption('pizza', 'pickup_enabled')}
                  className={`p-2 rounded-full ${
                    orderConfig.pizza.pickup_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <SafeIcon
                    icon={orderConfig.pizza.pickup_enabled ? FiToggleRight : FiToggleLeft}
                    className="text-2xl"
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div>
                  <h4 className="font-semibold text-lg">Lieferung</h4>
                  <p className="text-gray-600 text-sm">Ermöglicht Kunden, ihre Pizza liefern zu lassen</p>
                </div>
                <button
                  onClick={() => toggleOption('pizza', 'delivery_enabled')}
                  className={`p-2 rounded-full ${
                    orderConfig.pizza.delivery_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <SafeIcon
                    icon={orderConfig.pizza.delivery_enabled ? FiToggleRight : FiToggleLeft}
                    className="text-2xl"
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveOrderConfig}
              disabled={saving}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
            >
              <SafeIcon
                icon={saving ? FiLoader : FiSave}
                className={`mr-2 ${saving ? 'animate-spin' : ''}`}
              />
              {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
            </button>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">📝 Hinweis zur Bestelloptionen:</h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc pl-5">
              <li>Aktivieren oder deaktivieren Sie Abholung und Lieferung für jeden Produkttyp separat.</li>
              <li>Wenn beide Optionen deaktiviert sind, wird der Abschnitt "Bestelldetails" komplett ausgeblendet.</li>
              <li>Änderungen werden sofort im Frontend sichtbar, nachdem sie gespeichert wurden.</li>
              <li>Die Einstellungen gelten für alle Besucher der Website.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderOptionsAdmin;